/// ApprovalForm: Built-in user task form for approval workflows.
/// Shows task variables, a comment textarea, and Approve/Reject/Fail buttons.
/// Completes the job with { approved: boolean, comment: string } variables.

import { useState } from "react";
import type { TaskFormProps } from "../TaskFormRouter";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ApprovalForm({ task, onComplete, onFail }: TaskFormProps) {
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await onComplete({ approved: true, comment });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      await onComplete({ approved: false, comment });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFail = async () => {
    setIsSubmitting(true);
    try {
      await onFail("Task failed by user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Task variables display */}
      <div>
        <label className="text-xs text-zinc-400 mb-1 block">Task Variables</label>
        <pre className="bg-zinc-800 rounded p-3 text-xs text-zinc-300 max-h-32 overflow-y-auto font-mono">
          {JSON.stringify(task.variables, null, 2)}
        </pre>
      </div>

      {/* Comment input */}
      <div>
        <label className="text-xs text-zinc-400 mb-1 block">Comment</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="w-full h-20 rounded bg-zinc-800 p-2 text-sm text-zinc-200 focus:outline-none border border-zinc-600 resize-none"
          placeholder="Optional comment..."
        />
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleApprove}
          disabled={isSubmitting}
          className="flex-1 bg-green-600 text-white py-2 rounded text-sm hover:bg-green-500 disabled:opacity-50 transition-colors"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={handleReject}
          disabled={isSubmitting}
          className="flex-1 bg-yellow-600 text-white py-2 rounded text-sm hover:bg-yellow-500 disabled:opacity-50 transition-colors"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={handleFail}
          disabled={isSubmitting}
          className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-500 disabled:opacity-50 transition-colors"
        >
          Fail
        </button>
      </div>
    </div>
  );
}
